import { createPhotoProduct, PhotoProductViewModel } from './photoProduct';
import { stickerSheet } from './stickerCropPreview';

// Geometry comes from PrintCropGeometry on the server, keyed by original size and product.
function getCrop(photo, product, previews) {
    if (!photo || !product) return null;
    return previews[`${photo.width}x${photo.height}:${product.id}`] || null;
}

function escapeAttribute(value) {
    return String(value ?? '').replace(/[&<>"']/g, char => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[char]);
}

export function createShopPhotoPreview(photo, product, effect, previews, imageLabel) {
    const crop = getCrop(photo, product, previews);
    if (product && (!crop || (product.grid !== 'Normal1X1' && !product.stickerCount))) {
        return createPhotoProduct(new PhotoProductViewModel(photo.url, product.grid,
            photo.orientation, effect, product.mockup, product.size, product.cardName));
    }

    if (crop && product?.stickerCount > 0) {
        const sheet = stickerSheet(photo, effect, crop, product.stickerCount, .033, .04, false);
        sheet.removeAttribute('aria-hidden');
        sheet.setAttribute('role', 'img');
        sheet.setAttribute('aria-label', imageLabel || '');
        return {
            html: `<div class="print-crop-preview sticker-sheet-preview" photo-product>${sheet.outerHTML}</div>`,
            resizeScript() {}
        };
    }

    return createPrintCropPhoto(photo, effect, crop, imageLabel);
}

export function createPrintCropPhoto(photo, effect, crop, imageLabel, showGuides = false, grid = 'Normal1X1') {
    // Result previews repeat the same production crop in every cell of the selected product.
    const counts = { Normal1X2: 2, Normal2X2: 4, Normal4X2: 8, Normal4X4: 16 };
    const count = counts[grid];
    if (!showGuides && count) {
        const area = crop ?? { x: 0, y: 0, width: photo.width, height: photo.height };
        const landscape = area.width > area.height;
        const columns = count === 2 ? (landscape ? 1 : 2) : count === 4 ? 2 : count === 8 && landscape ? 2 : 4;
        const sheet = stickerSheet(photo, effect, area, count, 0, 0, false, columns);
        sheet.removeAttribute('aria-hidden');
        sheet.setAttribute('role', 'img');
        sheet.setAttribute('aria-label', imageLabel || '');
        return { html: `<div class="print-crop-preview" photo-product>${sheet.outerHTML}</div>`, resizeScript() {} };
    }
    const filter = effect === 'Sepia' ? 'sepia(100%)' : effect === 'BlackWhite' ? 'grayscale(100%)' : 'none';
    const url = escapeAttribute(photo.url);
    const label = escapeAttribute(imageLabel);
    const width = photo.width, height = photo.height;
    if (!(width > 0 && height > 0)) {
        return {
            html: `<div class="print-crop-preview" photo-product><img src="${url}" alt="${label}" style="filter:${filter}" /></div>`,
            resizeScript() {}
        };
    }

    let overlay = '';
    if (crop && showGuides) {
        const right = crop.x + crop.width, bottom = crop.y + crop.height;
        const masks = [
            [0, 0, width, crop.y], [0, bottom, width, height - bottom],
            [0, crop.y, crop.x, crop.height], [right, crop.y, width - right, crop.height]
        ].filter(([, , w, h]) => w > 0 && h > 0)
            .map(([x, y, w, h]) => `<rect class="print-crop-mask" x="${x}" y="${y}" width="${w}" height="${h}" />`).join('');
        overlay = `${masks}<rect class="print-crop-frame-contrast" x="${crop.x}" y="${crop.y}" width="${crop.width}" height="${crop.height}" vector-effect="non-scaling-stroke" /><rect class="print-crop-frame" x="${crop.x}" y="${crop.y}" width="${crop.width}" height="${crop.height}" vector-effect="non-scaling-stroke" />`;
    }

    // Shopping shows the finished print; only the cart shows the original with guides.
    const viewBox = crop && !showGuides
        ? `${crop.x} ${crop.y} ${crop.width} ${crop.height}` : `0 0 ${width} ${height}`;
    // Apply effects to the HTML wrapper: WebKit may ignore CSS filters on SVG image nodes.
    const image = `<image href="${url}" width="${width}" height="${height}" preserveAspectRatio="none" />`;
    const photoMarkup = crop && !showGuides
        ? `<svg x="${crop.x}" y="${crop.y}" width="${crop.width}" height="${crop.height}" viewBox="${viewBox}" style="overflow:hidden">${image}</svg>` : image;
    return {
        html: `<div class="print-crop-preview" photo-product style="filter:${filter}">
            <svg viewBox="${viewBox}" overflow="hidden" preserveAspectRatio="xMidYMid meet" role="img" aria-label="${label}">
                ${photoMarkup}
                ${showGuides ? `<rect class="print-crop-original-edge" width="${width}" height="${height}" vector-effect="non-scaling-stroke" />` : ''}
                ${overlay}
            </svg>
        </div>`,
        resizeScript() {}
    };
}

export function updatePrintCropInfo(info, photo, product, previews) {
    // Crop review and its explanations belong to the cart, not product selection.
    if (info) info.hidden = true;
}

function setPrintCropInfo(info, crop) {
    if (!info) return;
    info.hidden = !crop;
    if (!crop) return;
    const hasCrop = crop.hasHorizontalCrop || crop.hasVerticalCrop;
    info.classList.toggle('print-crop-info--complete', !hasCrop);
    info.querySelector('[data-print-crop-symbol]').textContent = hasCrop ? '' : '✓';
    const text = crop.hasVerticalCrop ? info.dataset.verticalLabel
        : crop.hasHorizontalCrop ? info.dataset.horizontalLabel : info.dataset.completeLabel;
    info.querySelector('[data-print-crop-text]').textContent = text;
}

// Initial tiles share geometry with interactive previews. Review guides are cart-only.
export function initializePrintCropPhotos(root) {
    if (!root) return;
    root.querySelectorAll('[data-print-crop-photo]').forEach(host => {
        const preview = JSON.parse(host.dataset.printCropPhoto);
        const info = document.getElementById(preview.infoId);
        const imageLabel = host.querySelector('img')?.alt || info?.dataset.imageLabel || '';
        const showGuides = !!host.closest('.cart-print-crop');
        if (preview.stickerCount > 0) {
            host.classList.add('sticker-sheet-preview');
            const sheet = stickerSheet(preview.photo, preview.effect, preview.crop,
                preview.stickerCount, preview.stickerInset, preview.stickerRadius, showGuides);
            sheet.removeAttribute('aria-hidden');
            sheet.setAttribute('role', 'img');
            sheet.setAttribute('aria-label', imageLabel);
            host.replaceChildren(sheet);
            host.removeAttribute('data-print-crop-photo');
            return;
        }
        const node = createPrintCropPhoto(preview.photo, preview.effect, preview.crop, imageLabel, showGuides, preview.grid);
        host.innerHTML = node.html;
        setPrintCropInfo(info, showGuides ? preview.crop : null);
        host.removeAttribute('data-print-crop-photo');
    });
}
